import React, { useState } from "react";
import { ActivityChangeDetail } from "@api/graphql/generated/generated-types";
import { diffSnapshotEntries } from "../utils/diffSnapshotEntries";
import CourieButton from "@src/common/components/Button/Button";
import { ActivityChangeDetailModal } from "./ActivityChangeDetailModal";

interface ActivityChangeDetailProps {
  change: ActivityChangeDetail;
}

export function ActivityChangeDetailView({
  change,
}: ActivityChangeDetailProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!change) return null;

  // Show value if entries are null but value exists
  if (!change.old?.entries && !change.new?.entries && change.new?.value) {
    return (
      <div className="mt-2 border dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900/30 p-2">
        <div className="flex flex-col gap-2 mt-1">
          <div className="flex-1 flex flex-col">
            <div className="flex-1 text-xs text-slate-400 dark:text-gray-500">
              Change
            </div>
            <div className="flex-1 text-xs font-bold text-gray-900 dark:text-gray-100">
              {change.new.value}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const diffs = diffSnapshotEntries(change.old, change.new);
  const changedDiffs = diffs.filter(({ changed }) => changed);
  if (!changedDiffs.length) return null;

  const MAX_VISIBLE = 3;
  const visibleDiffs = isExpanded
    ? changedDiffs
    : changedDiffs.slice(0, MAX_VISIBLE);
  const hasMore = changedDiffs.length > MAX_VISIBLE;

  return (
    <div className="mt-2 border dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900/30 p-2">
      <div className="flex flex-col gap-2 mt-1">
        {visibleDiffs.map(({ key, newValue }) => (
          <div key={key} className="flex-1 flex flex-col">
            <div className="flex-1 text-xs text-slate-400 dark:text-gray-500">
              {key}
            </div>
            <div className="flex-1 text-xs font-bold text-gray-900 dark:text-gray-100">
              {newValue ?? "-"}
            </div>
          </div>
        ))}
        <div className="flex gap-2 mt-1">
          {hasMore && (
            <CourieButton
              color="gray"
              size="xs"
              className="w-fit"
              onClick={() => setIsExpanded((v) => !v)}
            >
              {isExpanded
                ? "See less"
                : `See more (${changedDiffs.length - MAX_VISIBLE})`}
            </CourieButton>
          )}
          <CourieButton
            color="gray"
            size="xs"
            className="w-fit"
            onClick={() => setIsModalOpen(true)}
          >
            Compare with Previous
          </CourieButton>
        </div>
      </div>
      <ActivityChangeDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        changedDiffs={changedDiffs}
      />
    </div>
  );
}
